import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, IconButton,
    TextField, InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    LockOutlined, SearchOutlined, AddOutlined,
    BookmarkOutlined, BookmarkBorderOutlined, CloseOutlined,
    NorthEastOutlined, SouthWestOutlined, TrendingUpOutlined,
    TrendingDownOutlined, ShowChartOutlined, AutoGraphOutlined,
    NotificationsNoneOutlined, ArrowBackOutlined,
} from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { B, NGX_STOCKS, MUTUAL_FUNDS } from './investData';

const MiniChart: React.FC<{ data: number[]; positive: boolean }> = ({ data, positive }) => (
    <ResponsiveContainer width={64} height={30}>
        <LineChart data={data.map((v, i) => ({ v, i }))}>
            <Line type="monotone" dataKey="v" stroke={positive ? B.green : B.red} strokeWidth={1.5} dot={false} />
        </LineChart>
    </ResponsiveContainer>
);

/* ── Demo watchlist state ── */
const DEFAULT_STOCK_IDS = ['gtco', 'seplat', 'mtn-nigeria'];
const DEFAULT_FUND_IDS  = ['arm-discovery', 'cowrywise-dollar'];

const ALERTS_DEMO = [
    { id: 'a1', asset: 'GTCO',        type: 'Price below', value: '₦55.00',  active: true  },
    { id: 'a2', asset: 'Seplat Energy',type: 'Price above', value: '₦6,000', active: false },
];

const WatchlistPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [tab, setTab]               = useState<'stocks'|'funds'>('stocks');
    const [watchStocks, setWatchStocks] = useState<string[]>(DEFAULT_STOCK_IDS);
    const [watchFunds, setWatchFunds]   = useState<string[]>(DEFAULT_FUND_IDS);
    const [search, setSearch]          = useState('');
    const [showAdd, setShowAdd]        = useState(false);

    const pageBg  = isDark ? B.deep : B.lavender;
    const surf    = isDark ? alpha(B.mid, .55) : '#fff';
    const bdr     = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const shd     = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri = isDark ? '#fff' : B.ink;
    const textMid = alpha(isDark ? '#fff' : B.ink, .45);
    const textFnt = alpha(isDark ? '#fff' : B.ink, .28);
    const divC    = alpha(isDark ? '#fff' : B.dark, .08);
    const glass   = { background: surf, border: bdr, boxShadow: shd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    const watchedStocks = NGX_STOCKS.filter(s => watchStocks.includes(s.id));
    const watchedFunds  = MUTUAL_FUNDS.filter(f => watchFunds.includes(f.id));

    const allStocks = NGX_STOCKS.filter(s =>
        !watchStocks.includes(s.id) &&
        (s.name.toLowerCase().includes(search.toLowerCase()) || s.ticker.toLowerCase().includes(search.toLowerCase()))
    );
    const allFunds = MUTUAL_FUNDS.filter(f =>
        !watchFunds.includes(f.id) &&
        f.name.toLowerCase().includes(search.toLowerCase())
    );

    const toggleStock = (id: string) => setWatchStocks(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    const toggleFund  = (id: string) => setWatchFunds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    return (
        <Box sx={{ minHeight:'100vh', background:pageBg, pb:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>

            {/* Hero */}
            <Box sx={{ background:`linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`, mt:{ xs:'-76px', sm:0 }, pt:{ xs:'80px', sm:'28px' }, pb:{ xs:4, sm:14 }, px:3, position:'relative', overflow:'hidden' }}>
                <Box sx={{ position:'absolute', top:-60, right:-40, width:200, height:200, borderRadius:'50%', background:`radial-gradient(circle, ${alpha(B.gold,.12)} 0%, transparent 70%)`, pointerEvents:'none' }} />
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton size="small" onClick={() => navigate(-1)} sx={{ color:alpha('#fff',.6), background:alpha('#fff',.08), '&:hover':{ background:alpha('#fff',.16) } }}>
                            <ArrowBackOutlined sx={{ fontSize:18 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color:alpha('#fff',.35), fontSize:'.62rem', letterSpacing:'.18em', textTransform:'uppercase' }}>NG Credit</Typography>
                            <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1.15rem', fontFamily:"'Instrument Serif',serif" }}>Watchlist</Typography>
                        </Box>
                    </Box>
                    <Button onClick={() => setShowAdd(true)} startIcon={<AddOutlined />} sx={{ background:alpha('#fff',.15), backdropFilter:'blur(8px)', border:`1px solid ${alpha('#fff',.2)}`, borderRadius:'11px', textTransform:'none', fontWeight:700, fontSize:'.78rem', color:'#fff', boxShadow:'none' }}>Add</Button>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt:{ xs:2, sm:-8 }, position:'relative', zIndex:1, px:{ xs:2, sm:3 } }}>

                {/* Summary strip */}
                <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1.25, mb:2 }}>
                    {[
                        { label:'Watching', value:`${watchedStocks.length + watchedFunds.length}`, color:textPri },
                        { label:'Gainers',  value:`${watchedStocks.filter(s=>s.change>0).length}`,  color:B.green },
                        { label:'Losers',   value:`${watchedStocks.filter(s=>s.change<0).length}`,  color:B.red   },
                    ].map(s => (
                        <Box key={s.label} sx={{ ...glass, p:1.75, textAlign:'center' }}>
                            <Typography sx={{ color:s.color, fontFamily:"'Instrument Serif',serif", fontSize:'1.3rem', lineHeight:1.2 }}>{s.value}</Typography>
                            <Typography sx={{ color:textFnt, fontSize:'.62rem', mt:.3 }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Tab strip */}
                <Box sx={{ ...glass, p:.7, mb:2, display:'flex', gap:.5 }}>
                    {(['stocks','funds'] as const).map(t => {
                        const active = tab === t;
                        return (
                            <Box key={t} onClick={() => setTab(t)} sx={{ flex:1, py:1, borderRadius:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:.6, background: active ? `linear-gradient(135deg,${B.purple},${B.light})` : 'transparent', boxShadow: active ? `0 4px 16px ${alpha(B.purple,.32)}` : 'none', transition:'all .22s' }}>
                                {t==='stocks' ? <ShowChartOutlined sx={{ fontSize:14, color:active?'#fff':textFnt }} /> : <AutoGraphOutlined sx={{ fontSize:14, color:active?'#fff':textFnt }} />}
                                <Typography sx={{ color:active?'#fff':textFnt, fontWeight:700, fontSize:'.75rem', textTransform:'capitalize' }}>{t}</Typography>
                                <Box sx={{ px:.65, py:.15, borderRadius:8, background:alpha(active?'#fff':B.purple,.15) }}>
                                    <Typography sx={{ color:active?B.purple:'#fff', fontSize:'.58rem', fontWeight:700 }}>{t==='stocks'?watchedStocks.length:watchedFunds.length}</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Stocks tab */}
                {tab==='stocks' && (
                    watchedStocks.length === 0 ? (
                        <Box sx={{ ...glass, p:4, textAlign:'center' }}>
                            <BookmarkBorderOutlined sx={{ fontSize:40, color:textFnt, mb:1.5 }} />
                            <Typography sx={{ color:textPri, fontWeight:700, mb:.5 }}>No stocks yet</Typography>
                            <Typography sx={{ color:textMid, fontSize:'.8rem', mb:2 }}>Add stocks from the NGX to track their price movements.</Typography>
                            <Button variant="contained" onClick={() => setShowAdd(true)} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'12px', textTransform:'none', fontWeight:700, boxShadow:'none' }}>Browse Stocks</Button>
                        </Box>
                    ) : (
                        <Box sx={{ ...glass, overflow:'hidden' }}>
                            {watchedStocks.map((stock, i) => (
                                <Box key={stock.id}>
                                    <Box sx={{ px:2.5, py:1.75, display:'flex', alignItems:'center', gap:1.5, cursor:'pointer', transition:'background .15s', '&:hover':{ background:alpha(isDark?'#fff':B.dark,.025) } }}>
                                        <Box onClick={() => navigate(`/invest/stock/${stock.id}`)} sx={{ display:'flex', alignItems:'center', gap:1.5, flex:1, minWidth:0 }}>
                                            <Box sx={{ width:44, height:44, borderRadius:'13px', background:alpha(stock.color,.1), border:`1px solid ${alpha(stock.color,.2)}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                <Typography sx={{ color:stock.color, fontWeight:800, fontSize:stock.ticker.length>6?'.5rem':'.62rem', textAlign:'center', lineHeight:1.2 }}>{stock.ticker.slice(0,6)}</Typography>
                                            </Box>
                                            <Box flex={1} minWidth={0}>
                                                <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.84rem' }} noWrap>{stock.name}</Typography>
                                                <Typography sx={{ color:textFnt, fontSize:'.68rem' }}>{stock.sector}</Typography>
                                            </Box>
                                        </Box>
                                        <MiniChart data={stock.chart} positive={stock.change>0} />
                                        <Box sx={{ textAlign:'right', flexShrink:0 }}>
                                            <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.86rem' }}>₦{stock.price.toLocaleString()}</Typography>
                                            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:.3 }}>
                                                {stock.change>0 ? <TrendingUpOutlined sx={{ color:B.green, fontSize:12 }} /> : <TrendingDownOutlined sx={{ color:B.red, fontSize:12 }} />}
                                                <Typography sx={{ color:stock.change>0?B.green:B.red, fontSize:'.72rem', fontWeight:700 }}>{stock.change>0?'+':''}{stock.change}%</Typography>
                                            </Box>
                                        </Box>
                                        <IconButton size="small" onClick={() => toggleStock(stock.id)} sx={{ color:B.purple, ml:.5 }}>
                                            <BookmarkOutlined sx={{ fontSize:18 }} />
                                        </IconButton>
                                    </Box>
                                    {i < watchedStocks.length - 1 && <Box sx={{ mx:2.5, height:'1px', background:divC }} />}
                                </Box>
                            ))}
                        </Box>
                    )
                )}

                {/* Funds tab */}
                {tab==='funds' && (
                    watchedFunds.length === 0 ? (
                        <Box sx={{ ...glass, p:4, textAlign:'center' }}>
                            <BookmarkBorderOutlined sx={{ fontSize:40, color:textFnt, mb:1.5 }} />
                            <Typography sx={{ color:textPri, fontWeight:700, mb:.5 }}>No funds yet</Typography>
                            <Typography sx={{ color:textMid, fontSize:'.8rem', mb:2 }}>Add mutual funds to monitor their performance.</Typography>
                            <Button variant="contained" onClick={() => setShowAdd(true)} sx={{ background:`linear-gradient(135deg,${B.purple},${B.light})`, borderRadius:'12px', textTransform:'none', fontWeight:700, boxShadow:'none' }}>Browse Funds</Button>
                        </Box>
                    ) : (
                        <Box sx={{ display:'flex', flexDirection:'column', gap:1.25 }}>
                            {watchedFunds.map(fund => (
                                <Box key={fund.id} sx={{ ...glass, p:2.5, cursor:'pointer', transition:'all .18s', '&:hover':{ transform:'translateY(-2px)', boxShadow:`0 12px 32px ${alpha(fund.color,.14)}` } }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box flex={1} pr={1} onClick={() => navigate(`/invest/fund/${fund.id}`)}>
                                            <Box display="flex" alignItems="center" gap={.75} mb={.5}>
                                                <Box sx={{ px:.9, py:.25, borderRadius:8, background:alpha(fund.riskColor,.1), border:`1px solid ${alpha(fund.riskColor,.2)}` }}>
                                                    <Typography sx={{ color:fund.riskColor, fontSize:'.58rem', fontWeight:700 }}>{fund.risk}</Typography>
                                                </Box>
                                                <Typography sx={{ color:textFnt, fontSize:'.65rem' }}>{fund.category}</Typography>
                                            </Box>
                                            <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.86rem', lineHeight:1.3 }}>{fund.name}</Typography>
                                            <Typography sx={{ color:textFnt, fontSize:'.7rem', mt:.3 }}>{fund.manager}</Typography>
                                        </Box>
                                        <Box sx={{ textAlign:'right', flexShrink:0 }}>
                                            <Typography sx={{ color:B.green, fontFamily:"'Instrument Serif',serif", fontSize:'1.3rem', lineHeight:1 }}>{fund.returns.yr1}%</Typography>
                                            <Typography sx={{ color:textFnt, fontSize:'.6rem' }}>1yr</Typography>
                                            <IconButton size="small" onClick={() => toggleFund(fund.id)} sx={{ color:B.purple, mt:.5 }}>
                                                <BookmarkOutlined sx={{ fontSize:18 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Box sx={{ mt:1.5, height:2.5, borderRadius:4, background:`linear-gradient(90deg,${fund.color},${alpha(fund.color,.15)})` }} />
                                </Box>
                            ))}
                        </Box>
                    )
                )}

                {/* Price alerts */}
                <Box sx={{ ...glass, p:2, mt:2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <NotificationsNoneOutlined sx={{ color:B.purple, fontSize:18 }} />
                            <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.85rem' }}>Price Alerts</Typography>
                        </Box>
                        <Button size="small" sx={{ color:B.purple, textTransform:'none', fontWeight:600, fontSize:'.72rem' }}>+ Add</Button>
                    </Box>
                    {ALERTS_DEMO.map((alert, i) => (
                        <Box key={alert.id} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', py:1, borderBottom: i<ALERTS_DEMO.length-1 ? `1px solid ${divC}` : 'none' }}>
                            <Box>
                                <Typography sx={{ color:textPri, fontWeight:600, fontSize:'.78rem' }}>{alert.asset}</Typography>
                                <Typography sx={{ color:textFnt, fontSize:'.68rem' }}>{alert.type} {alert.value}</Typography>
                            </Box>
                            <Box sx={{ px:1, py:.35, borderRadius:20, background: alert.active ? alpha(B.green,.1) : alpha(isDark?'#fff':B.dark,.05), border:`1px solid ${alert.active ? alpha(B.green,.2) : divC}` }}>
                                <Typography sx={{ color: alert.active ? B.green : textFnt, fontSize:'.62rem', fontWeight:700 }}>{alert.active ? 'ACTIVE' : 'OFF'}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ mt:3.5, display:'flex', alignItems:'center', justifyContent:'center', gap:.75 }}>
                    <LockOutlined sx={{ fontSize:11, color:alpha(isDark?'#fff':B.ink,.18) }} />
                    <Typography sx={{ fontSize:'.62rem', color:alpha(isDark?'#fff':B.ink,.18), letterSpacing:'.05em' }}>256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES</Typography>
                </Box>
            </Container>

            {/* Add drawer overlay */}
            {showAdd && (
                <Box sx={{ position:'fixed', inset:0, zIndex:1400, background:alpha(B.deep,.7), backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end' }} onClick={() => setShowAdd(false)}>
                    <Box onClick={e => e.stopPropagation()} sx={{ width:'100%', maxWidth:600, mx:'auto', background: isDark ? B.dark : '#fff', borderRadius:'24px 24px 0 0', p:3, pb:5, maxHeight:'80vh', overflow:'auto' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography sx={{ color:textPri, fontWeight:700, fontSize:'1rem' }}>Add to Watchlist</Typography>
                            <IconButton onClick={() => setShowAdd(false)} size="small" sx={{ color:textMid }}><CloseOutlined /></IconButton>
                        </Box>

                        <TextField fullWidth placeholder="Search stocks or funds…" value={search} onChange={e => setSearch(e.target.value)} size="small"
                                   InputProps={{ startAdornment:<InputAdornment position="start"><SearchOutlined sx={{ color:textFnt, fontSize:18 }} /></InputAdornment>, sx:{ borderRadius:'12px', color:textPri, background:alpha(isDark?'#fff':B.dark,.05) } }}
                                   sx={{ mb:2, '& .MuiOutlinedInput-notchedOutline':{ border:`1px solid ${divC}` } }} />

                        {/* Stocks section */}
                        {(tab==='stocks' || search) && allStocks.length > 0 && <>
                            <Typography sx={{ color:textFnt, fontSize:'.65rem', letterSpacing:'.1em', textTransform:'uppercase', fontWeight:600, mb:1 }}>Stocks</Typography>
                            {allStocks.map(stock => (
                                <Box key={stock.id} sx={{ display:'flex', alignItems:'center', gap:1.5, py:1.1, borderBottom:`1px solid ${divC}` }}>
                                    <Box sx={{ width:38, height:38, borderRadius:'11px', background:alpha(stock.color,.1), border:`1px solid ${alpha(stock.color,.2)}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                        <Typography sx={{ color:stock.color, fontWeight:800, fontSize:'.55rem', textAlign:'center' }}>{stock.ticker.slice(0,6)}</Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography sx={{ color:textPri, fontWeight:600, fontSize:'.82rem' }}>{stock.name}</Typography>
                                        <Typography sx={{ color:textFnt, fontSize:'.66rem' }}>₦{stock.price.toLocaleString()} · {stock.change>0?'+':''}{stock.change}%</Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => toggleStock(stock.id)} sx={{ color:textFnt, '&:hover':{ color:B.purple } }}>
                                        <BookmarkBorderOutlined sx={{ fontSize:20 }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </>}

                        {/* Funds section */}
                        {(tab==='funds' || search) && allFunds.length > 0 && <>
                            <Typography sx={{ color:textFnt, fontSize:'.65rem', letterSpacing:'.1em', textTransform:'uppercase', fontWeight:600, mb:1, mt:2 }}>Mutual Funds</Typography>
                            {allFunds.map(fund => (
                                <Box key={fund.id} sx={{ display:'flex', alignItems:'center', gap:1.5, py:1.1, borderBottom:`1px solid ${divC}` }}>
                                    <Box sx={{ width:38, height:38, borderRadius:'11px', background:alpha(fund.color,.1), display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                        <AutoGraphOutlined sx={{ color:fund.color, fontSize:18 }} />
                                    </Box>
                                    <Box flex={1} minWidth={0}>
                                        <Typography sx={{ color:textPri, fontWeight:600, fontSize:'.8rem' }} noWrap>{fund.name}</Typography>
                                        <Typography sx={{ color:textFnt, fontSize:'.66rem' }}>{fund.returns.yr1}% p.a. · {fund.risk} risk</Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => toggleFund(fund.id)} sx={{ color:textFnt, '&:hover':{ color:B.purple } }}>
                                        <BookmarkBorderOutlined sx={{ fontSize:20 }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </>}

                        {allStocks.length===0 && allFunds.length===0 && search && (
                            <Typography sx={{ color:textMid, textAlign:'center', py:3, fontSize:'.85rem' }}>No results for "{search}"</Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WatchlistPage;